import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import heroMobileBg from '@/assets/hero-mobile-bg.jpeg';

const Hero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const isMobile = useIsMobile();
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  // Defer video loading until after initial paint (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const id = requestIdleCallback?.(() => setShouldLoadVideo(true)) 
      ?? setTimeout(() => setShouldLoadVideo(true), 100);
    return () => {
      if (typeof id === 'number' && 'cancelIdleCallback' in window) {
        cancelIdleCallback(id);
      } else {
        clearTimeout(id as unknown as number);
      }
    };
  }, [isMobile]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && shouldLoadVideo) {
      video.play().catch(() => {});
    }
  }, [shouldLoadVideo]);

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video Background (desktop only) */}
      {!isMobile && shouldLoadVideo && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          muted
          loop
          playsInline
          preload="metadata"
          onCanPlayThrough={() => setVideoLoaded(true)}
          onError={() => {}}
        >
          <source src="/videos/hero-bg.webm" type="video/webm" onError={() => {}} />
        </video>
      )}

      {/* Fallback/Mobile Background */}
      {isMobile ? (
        <img
          src={heroMobileBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
      ) : (
        <div 
          className={`absolute inset-0 bg-gradient-to-br from-background via-background to-secondary ${
            videoLoaded ? 'opacity-0' : 'opacity-100'
          } transition-opacity duration-1000`}
        />
      )}

      {/* Dark Overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          {/* Badge */}
          {isMobile ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-8">
              <span className="w-2 h-2 rounded-full bg-primary" />
              <span className="text-sm font-medium text-primary">Fitness & Wellness Industry Marketing Specialists</span>
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-medium text-primary">Fitness & Wellness Industry Marketing Specialists</span>
            </motion.div>
          )}

          {/* Main Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
            Marketing That Hits The{' '}
            <span className="text-primary italic">Bullseye</span>
          </h1>

          {/* Subheadline */}
          {isMobile ? (
            <p className="text-lg text-muted-foreground mb-10">
              We help fitness and wellness brands attract their ideal clients, 
              build powerful communities, and scale with precision-targeted marketing 
              that delivers measurable results.
            </p>
          ) : (
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg md:text-xl text-muted-foreground mb-10"
            >
              We help fitness and wellness brands attract their ideal clients, 
              build powerful communities, and scale with precision-targeted marketing 
              that delivers measurable results.
            </motion.p>
          )}

          {/* CTA Buttons */}
          {isMobile ? (
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a href="https://www.cal.eu/archer-marketing" target="_blank" rel="noopener noreferrer">
                <Button variant="hero" size="xl">
                  Book a Discovery Call
                </Button>
              </a>
              <Button 
                variant="hero-ghost" 
                size="xl" 
                onClick={scrollToServices}
              >
                See Our Services
              </Button>
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="flex flex-col sm:flex-row items-start gap-4"
            >
              <a href="https://www.cal.eu/archer-marketing" target="_blank" rel="noopener noreferrer">
                <Button variant="hero" size="xl">
                  Book a Discovery Call
                </Button>
              </a>
              <Button 
                variant="hero-ghost" 
                size="xl" 
                onClick={scrollToServices}
              >
                See Our Services
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Scroll Indicator (desktop only) */}
      {!isMobile && (
        <motion.button
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          onClick={scrollToServices}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground transition-colors scroll-indicator"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5" />
        </motion.button>
      )}
    </section>
  );
};

export default Hero;
